import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Modal } from '../components/Modal';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../components/Field';
import { Alert, Spinner } from '../components/ui';
import { formatSwimTimeShort } from '../lib/format';
import {
  ENTRY_STATUS_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  type AdminEvent,
  type Entry,
} from '../lib/types';

interface Props {
  event: AdminEvent;
  /** Null creates a new entry. */
  entry: Entry | null;
  onClose: () => void;
  onSaved: (entry: Entry, heatsStale: boolean) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  club: string;
  categoryId: string;
  seedMinutes: string;
  seedSeconds: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalNotes: string;
  status: string;
  paymentStatus: string;
  paymentReference: string;
  adminNotes: string;
  consentMedia: boolean;
}

function initialState(entry: Entry | null, event: AdminEvent): FormState {
  if (!entry) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      club: '',
      categoryId: event.categories[0]?.id ?? '',
      seedMinutes: '',
      seedSeconds: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      medicalNotes: '',
      status: 'confirmed',
      paymentStatus: 'unpaid',
      paymentReference: '',
      adminNotes: '',
      consentMedia: false,
    };
  }

  const totalSeconds = Math.round(entry.seedTimeMs / 1000);
  return {
    firstName: entry.firstName,
    lastName: entry.lastName,
    email: entry.email,
    phone: entry.phone,
    dateOfBirth: entry.dateOfBirth,
    gender: entry.gender,
    club: entry.club,
    categoryId: entry.categoryId,
    seedMinutes: String(Math.floor(totalSeconds / 60)),
    seedSeconds: String(totalSeconds % 60),
    emergencyContactName: entry.emergencyContactName,
    emergencyContactPhone: entry.emergencyContactPhone,
    medicalNotes: entry.medicalNotes,
    status: entry.status,
    paymentStatus: entry.paymentStatus,
    paymentReference: entry.paymentReference,
    adminNotes: entry.adminNotes,
    consentMedia: entry.consentMedia,
  };
}

/** Create or edit a single entry. Server field errors are shown inline. */
export function EntryFormModal({ event, entry, onClose, onSaved }: Props) {
  const [form, setForm] = useState<FormState>(() => initialState(entry, event));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isNew = entry === null;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key] && !prev['seedTime']) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === 'seedMinutes' || key === 'seedSeconds') delete next['seedTime'];
      return next;
    });
  };

  const seedTimeMs = useMemo(() => {
    if (form.seedMinutes === '') return null;
    const minutes = Number(form.seedMinutes);
    const seconds = Number(form.seedSeconds || '0');
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
    return minutes * 60_000 + seconds * 1000;
  }, [form.seedMinutes, form.seedSeconds]);

  const seedTimeText =
    seedTimeMs === null
      ? ''
      : `${Number(form.seedMinutes)}:${String(Number(form.seedSeconds || 0)).padStart(2, '0')}`;

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    if (!form.firstName.trim()) found['firstName'] = 'Required';
    if (!form.lastName.trim()) found['lastName'] = 'Required';
    if (!/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(form.email.trim())) {
      found['email'] = 'Enter a valid email address';
    }
    if (!form.phone.trim()) found['phone'] = 'Required';
    if (!form.dateOfBirth) found['dateOfBirth'] = 'Required';
    if (!form.gender) found['gender'] = 'Required';
    if (!form.categoryId) found['categoryId'] = 'Required';
    if (seedTimeMs === null) found['seedMinutes'] = 'Enter a predicted time';
    else if (Number(form.seedSeconds || 0) > 59) found['seedSeconds'] = 'Must be 0-59';
    else if (seedTimeMs < event.minSeedTimeMs) {
      found['seedMinutes'] = `Must be slower than ${formatSwimTimeShort(event.minSeedTimeMs)}`;
    } else if (seedTimeMs > event.maxSeedTimeMs) {
      found['seedMinutes'] = `Must be faster than ${formatSwimTimeShort(event.maxSeedTimeMs)}`;
    }
    if (!form.emergencyContactName.trim()) found['emergencyContactName'] = 'Required';
    if (!form.emergencyContactPhone.trim()) found['emergencyContactPhone'] = 'Required';
    return found;
  }

  async function onSubmit(formEvent: React.FormEvent) {
    formEvent.preventDefault();
    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      dateOfBirth: form.dateOfBirth,
      gender: form.gender,
      club: form.club.trim(),
      categoryId: form.categoryId,
      seedTime: seedTimeText,
      emergencyContactName: form.emergencyContactName.trim(),
      emergencyContactPhone: form.emergencyContactPhone.trim(),
      medicalNotes: form.medicalNotes.trim(),
      status: form.status,
      paymentStatus: form.paymentStatus,
      paymentReference: form.paymentReference.trim(),
      adminNotes: form.adminNotes.trim(),
      consentMedia: form.consentMedia,
    };

    try {
      if (isNew) {
        const created = await api.admin.createEntry({ ...payload, consentRules: true });
        onSaved(created, false);
      } else {
        const updated = await api.admin.updateEntry(entry.entryId, payload);
        onSaved(updated.entry, updated.heatsStale);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        // "seedTime" maps onto the minutes input.
        const mapped: Record<string, string> = {};
        for (const [field, message] of Object.entries(err.fields)) {
          mapped[field === 'seedTime' ? 'seedMinutes' : field] = message;
        }
        setErrors(mapped);
      } else {
        setFormError('Could not save the entry. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isNew ? 'Add an entry' : `Edit ${entry.firstName} ${entry.lastName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="entry-form" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner label="Saving" /> Saving...
              </>
            ) : isNew ? (
              'Create entry'
            ) : (
              'Save changes'
            )}
          </button>
        </>
      }
    >
      <form id="entry-form" className="stack" onSubmit={onSubmit} noValidate>
        {formError && (
          <Alert variant="error" title="Not saved">
            <p style={{ marginBottom: 0 }}>{formError}</p>
          </Alert>
        )}

        {!isNew && (
          <p className="faint" style={{ margin: 0 }}>
            Reference <strong className="mono">{entry.reference}</strong>
            {entry.lastEditedBy && <> &middot; last edited by {entry.lastEditedBy}</>}
          </p>
        )}

        <fieldset>
          <legend>Competitor</legend>
          <div className="form-grid">
            <TextField
              label="First name"
              required
              value={form.firstName}
              error={errors['firstName']}
              onChange={(e) => set('firstName', e.target.value)}
            />
            <TextField
              label="Last name"
              required
              value={form.lastName}
              error={errors['lastName']}
              onChange={(e) => set('lastName', e.target.value)}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={form.email}
              error={errors['email']}
              onChange={(e) => set('email', e.target.value)}
            />
            <TextField
              label="Phone"
              type="tel"
              required
              value={form.phone}
              error={errors['phone']}
              onChange={(e) => set('phone', e.target.value)}
            />
            <TextField
              label="Date of birth"
              type="date"
              required
              value={form.dateOfBirth}
              error={errors['dateOfBirth']}
              onChange={(e) => set('dateOfBirth', e.target.value)}
            />
            <SelectField
              label="Gender"
              required
              placeholder="Choose..."
              options={GENDER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              value={form.gender}
              error={errors['gender']}
              onChange={(e) => set('gender', e.target.value)}
            />
            <TextField
              label="Club"
              value={form.club}
              error={errors['club']}
              onChange={(e) => set('club', e.target.value)}
            />
          </div>
        </fieldset>

        <fieldset>
          <legend>Seeding</legend>
          <div className="form-grid">
            <SelectField
              label="Category"
              required
              options={event.categories.map((c) => ({ value: c.id, label: c.label }))}
              value={form.categoryId}
              error={errors['categoryId']}
              onChange={(e) => set('categoryId', e.target.value)}
            />
            <TextField
              label="Predicted minutes"
              type="number"
              min={0}
              max={180}
              required
              value={form.seedMinutes}
              error={errors['seedMinutes']}
              onChange={(e) => set('seedMinutes', e.target.value)}
            />
            <TextField
              label="Predicted seconds"
              type="number"
              min={0}
              max={59}
              value={form.seedSeconds}
              error={errors['seedSeconds']}
              onChange={(e) => set('seedSeconds', e.target.value)}
            />
          </div>
          {seedTimeMs !== null && (
            <p className="faint" style={{ margin: 'var(--space-3) 0 0' }}>
              Predicted time: <strong className="mono">{formatSwimTimeShort(seedTimeMs)}</strong>
            </p>
          )}
        </fieldset>

        <fieldset>
          <legend>Emergency contact and medical</legend>
          <div className="form-grid">
            <TextField
              label="Contact name"
              required
              value={form.emergencyContactName}
              error={errors['emergencyContactName']}
              onChange={(e) => set('emergencyContactName', e.target.value)}
            />
            <TextField
              label="Contact phone"
              type="tel"
              required
              value={form.emergencyContactPhone}
              error={errors['emergencyContactPhone']}
              onChange={(e) => set('emergencyContactPhone', e.target.value)}
            />
          </div>
          <TextAreaField
            label="Medical notes"
            value={form.medicalNotes}
            error={errors['medicalNotes']}
            maxLength={1000}
            onChange={(e) => set('medicalNotes', e.target.value)}
          />
        </fieldset>

        <fieldset>
          <legend>Administration</legend>
          <div className="form-grid">
            <SelectField
              label="Entry status"
              options={ENTRY_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              value={form.status}
              error={errors['status']}
              onChange={(e) => set('status', e.target.value)}
            />
            <SelectField
              label="Payment status"
              options={PAYMENT_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              value={form.paymentStatus}
              error={errors['paymentStatus']}
              onChange={(e) => set('paymentStatus', e.target.value)}
            />
            <TextField
              label="Payment reference"
              hint="Bank reference, receipt number or similar."
              value={form.paymentReference}
              error={errors['paymentReference']}
              onChange={(e) => set('paymentReference', e.target.value)}
            />
          </div>
          <TextAreaField
            label="Admin notes"
            hint="Internal only. Never shown to competitors."
            value={form.adminNotes}
            error={errors['adminNotes']}
            maxLength={2000}
            onChange={(e) => set('adminNotes', e.target.value)}
          />
          <CheckboxField
            label="Competitor consented to photography and video"
            checked={form.consentMedia}
            onChange={(checked) => set('consentMedia', checked)}
          />
        </fieldset>
      </form>
    </Modal>
  );
}
